// client/src/lib/api-helpers.ts
// Updated to ensure ALL authenticated endpoints use fetchWithAuth (no missing Authorization header)

import { useFetchWithAuth } from "@/lib/fetchWithAuth"; // ✅ fixed import
import { getSession } from "@/lib/sessionStore";

const apiCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 5000; // 5 seconds

/** Clear all cached API results — call when user identity changes */
export function clearApiCache() {
  apiCache.clear();
  console.log('[api-helpers] API cache cleared');
}

// ---------------------------------------------------------------------------
// INTERNAL FETCH WRAPPER (authentication is handled by fetchWithAuth)
// ---------------------------------------------------------------------------
async function apiGet(url: string) {
  const fetchWithAuth = useFetchWithAuth();

  if (!fetchWithAuth) {
    console.error("[api-helpers] fetchWithAuth not initialized!");
    throw new Error("Authentication system not ready.");
  }

  const res = await fetchWithAuth(url, {
    method: "GET",
    credentials: "include",
  });

  if (!res.ok) {
    const text = await res.text();
    console.error(`[api-helpers] GET ${url} failed:`, text);
    throw new Error(`Request failed: ${res.status}`);
  }

  return res.json();
}

// ---------------------------------------------------------------------------
// FETCH ALL HUBS MANAGED BY THE USER
// ---------------------------------------------------------------------------
export async function fetchManagedHubs() {
  const cacheKey = `managed-hubs:${getSession().user?.id || "anonymous"}`;
  const cached = apiCache.get(cacheKey);

  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }

  try {
    const timestamp = Date.now();
    const data = await apiGet(`/api/hubs/managed?_t=${timestamp}`);

    apiCache.set(cacheKey, { data, timestamp: Date.now() });
    return data;
  } catch (err) {
    console.error("Error fetching managed hubs:", err);
    throw new Error("Unable to fetch hubs. Please check your connection or API.");
  }
}

// ---------------------------------------------------------------------------
// FETCH CURRENT HUB
// ---------------------------------------------------------------------------
export async function fetchCurrentHub() {
  const cacheKey = `current-hub:${getSession().user?.id || "anonymous"}`;
  const cached = apiCache.get(cacheKey);

  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }

  try {
    const timestamp = Date.now();

    // Try to fetch current hub (may fail silently)
    let currentHub = null;
    try {
      currentHub = await apiGet(`/api/hubs/current?_t=${timestamp}`);
    } catch {
      console.warn("[fetchCurrentHub] No current hub assigned, falling back…");
    }

    // Fetch the full hubs list
    const hubs = await fetchManagedHubs();
    if (!hubs || hubs.length === 0) {
      throw new Error("No hubs available for this user.");
    }

    const finalHub = currentHub?.hub || hubs[0];

    const result = {
      hubId: finalHub.id,
      hub: finalHub,
    };

    apiCache.set(cacheKey, { data: result, timestamp: Date.now() });
    return result;
  } catch (err) {
    console.error("Error fetching current hub:", err);
    throw err;
  }
}

// ---------------------------------------------------------------------------
// SWITCH CURRENT HUB
// ---------------------------------------------------------------------------
export async function switchCurrentHub(hubId: string) {
  try {
    const hubs = await fetchManagedHubs();
    const exists = hubs.some((h) => h.id === hubId);

    if (!exists) {
      throw new Error("Invalid hub ID");
    }

    const fetchWithAuth = useFetchWithAuth();
    const res = await fetchWithAuth("/api/hubs/switch", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ hubId }),
    });

    if (!res.ok) {
      const text = await res.text();
      console.error("Failed to switch hub:", text);
      throw new Error(`Hub switch failed: ${res.status}`);
    }

    // Bust cache
    for (const key of apiCache.keys()) {
      if (key.startsWith("current-hub:")) apiCache.delete(key);
    }

    return true;
  } catch (err) {
    console.error("Error switching hub:", err);
    throw err;
  }
}
export async function safeApiRequest<T = any>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(url, { ...options, credentials: 'include' });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API request failed: ${res.status} ${res.statusText} - ${text}`);
  }
  return res.json() as Promise<T>;
}
